/**
 * JWT utility functions for token management
 */

interface JWTPayload {
    exp?: number; // Expiration time (seconds since Unix epoch)
    iat?: number; // Issued at time
    sub?: string; // Subject
    [key: string]: any; // Allow other claims
}

/**
 * Decode a JWT token without verification (client-side only)
 * Note: This is for extracting payload information only, not for verification
 */
export function decodeJWT(token: string): JWTPayload | null {
    try {
        // JWT has 3 parts separated by dots: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        // Decode the payload (second part)
        const payload = parts[1];
        
        // Add padding if needed for base64 decoding
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
        
        // Decode base64url to base64
        const base64 = paddedPayload.replace(/-/g, '+').replace(/_/g, '/');
        
        // Decode and parse JSON
        const decodedPayload = JSON.parse(atob(base64));
        
        return decodedPayload;
    } catch (error) {
        console.error('Error decoding JWT token:', error);
        return null;
    }
}

/**
 * Check if a JWT token is expired or will expire soon
 * @param token - The JWT token to check
 * @param bufferSeconds - Add buffer time before actual expiry (default: 300 seconds = 5 minutes)
 * @returns true if token is expired or will expire within buffer time
 */
export function isTokenExpiredOrExpiringSoon(token: string, bufferSeconds: number = 300): boolean {
    if (!token) {
        return true;
    }

    const payload = decodeJWT(token);
    if (!payload?.exp) {
        // If we can't get expiry, consider it expired for safety
        return true;
    }

    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const expiryWithBuffer = payload.exp - bufferSeconds;
    
    return now >= expiryWithBuffer;
}
